'use client';

import {
  FeatureSection as KeziosFeatureSection,
  IconDavinciResolve,
  IconHeartHand,
  IconMicrophone01,
  IconPremierPro,
  IconTranslate02,
  IconZapFast,
  InfoSection as KeziosInfoSection,
  Spacing,
} from '@kezios/design-system';
import { Button, Flex, Text } from '@mantine/core';
import { FEATURES } from 'app/(landing)/[lang]/[feature]/features.const';
import { CaptionVideo } from 'components/website/components/videos/CaptionVideo/CaptionVideo';
import { PodcastVideo } from 'components/website/components/videos/PodcastVideo/PodcastVideo';
import { ResizeVideo } from 'components/website/components/videos/ResizeVideo/ResizeVideo';
import { SilenceVideo } from 'components/website/components/videos/SilenceVideo/SilenceVideo';
import { ZoomVideo } from 'components/website/components/videos/ZoomVideo/ZoomVideo';
import { FeatureHeroSection } from 'components/website/sections/FeatureHeroSection/FeatureHeroSection';
import { InfoSectionLayout } from 'components/website/sections/InfoSection/InfoSectionLayout';
import Image from 'next/image';
import { Link, useTranslations } from 'next-intl';

const CompatibilityContentPremierePro = [
  <Flex key={1} align="center" gap={Spacing.s2}>
    <IconPremierPro size={24} />
    <Text variant="textXl.medium" c="gray.9">
      Premiere Pro
    </Text>
  </Flex>,
];

const CompatibilityContentPremiereProAndDavinciResolve = [
  <Flex key={1} align="center" gap={Spacing.s2}>
    <IconPremierPro size={24} />
    <Text variant="textLg.medium" c="gray.9">
      Premiere Pro
    </Text>
  </Flex>,
  <Flex key={1} align="center" gap={Spacing.s2}>
    <IconDavinciResolve size={24} />
    <Text variant="textLg.medium" c="gray.9">
      Davinci Resolve
    </Text>
  </Flex>,
];

const data: {
  [key in (typeof FEATURES)[number]]: {
    title: string;
    subtitle: string;
    demoUrl?: string;
    demoTitle?: string;
    videoComponent: (...props: any) => JSX.Element;
    compatibilities: React.ReactNode[];
    features: {
      iconComponent: JSX.Element;
      title: string;
      description: string;
      cta?: {
        title: string;
        url: string;
      };
    }[];
    tutorial: {
      title: string;
      steps: {
        title: string;
        description: string;
        iconComponent: JSX.Element;
        imagePath: JSX.Element;
        animatedTitle?: boolean;
      }[];
    };
  };
} = {
  autocaptions: {
    title: 'autocaptions.title',
    subtitle: 'autocaptions.subtitle',
    demoUrl: 'autocaptions.video.url',
    demoTitle: 'autocaptions.video.title',
    videoComponent: scrollPosition => (
      <CaptionVideo scrollPosition={scrollPosition} loop />
    ),
    compatibilities: CompatibilityContentPremiereProAndDavinciResolve,
    features: [
      {
        title: 'autocaptions.features.animations.title',
        description: 'autocaptions.features.animations.description',
        iconComponent: <IconZapFast size={24} />,
      },
      {
        title: 'autocaptions.features.emojis.title',
        description: 'autocaptions.features.emojis.description',
        iconComponent: <IconZapFast size={24} />,
      },
      {
        title: 'autocaptions.features.languages.title',
        description: 'autocaptions.features.languages.description',
        cta: {
          title: 'autocaptions.features.languages.ctaTitle',
          url: '/help-center/q-a/supported-languages/',
        },
        iconComponent: <IconTranslate02 size={24} />,
      },
    ],
    tutorial: {
      title: 'autocaptions.tutorial.title',
      steps: [
        {
          iconComponent: <IconHeartHand size={24} />,
          title: 'autocaptions.tutorial.steps.define.title',
          description: 'autocaptions.tutorial.steps.define.description',
          animatedTitle: true,
          imagePath: (
            <video key={`caption-autocut-1`} autoPlay muted loop playsInline>
              <source
                src={'/static/features/captions/captionsStep1.mp4'}
                type="video/mp4"
              />
            </video>
          ),
        },
        {
          iconComponent: <IconTranslate02 size={24} />,
          title: 'autocaptions.tutorial.steps.transcript.title',
          description: 'autocaptions.tutorial.steps.transcript.description',
          animatedTitle: true,
          imagePath: (
            <video key={`caption-autocut-2`} autoPlay muted loop playsInline>
              <source
                src={'/static/features/captions/captionsStep2.mp4'}
                type="video/mp4"
              />
            </video>
          ),
        },
        {
          iconComponent: <IconMicrophone01 size={24} />,
          title: 'autocaptions.tutorial.steps.relax.title',
          description: 'autocaptions.tutorial.steps.relax.description',
          animatedTitle: true,
          imagePath: (
            <video key={`caption-autocut-3`} autoPlay muted loop playsInline>
              <source
                src={'/static/features/captions/captionsStep32.mp4'}
                type="video/mp4"
              />
            </video>
          ),
        },
      ],
    },
  },
  autocutsilences: {
    title: 'autocutsilences.title',
    subtitle: 'autocutsilences.subtitle',
    demoUrl: 'autocutsilences.video.url',
    demoTitle: 'autocutsilences.video.title',
    videoComponent: scrollPosition => (
      <SilenceVideo scrollPosition={scrollPosition} loop />
    ),
    compatibilities: CompatibilityContentPremiereProAndDavinciResolve,
    features: [
      {
        title: 'autocutsilences.features.multitracks.title',
        description: 'autocutsilences.features.multitracks.description',
        iconComponent: <IconZapFast size={24} />,
      },
      {
        title: 'autocutsilences.features.formats.title',
        description: 'autocutsilences.features.formats.description',
        iconComponent: <IconZapFast size={24} />,
      },
      {
        title: 'autocutsilences.features.jcuts.title',
        description: 'autocutsilences.features.jcuts.description',
        iconComponent: <IconTranslate02 size={24} />,
      },
    ],
    tutorial: {
      title: 'autocutsilences.tutorial.title',
      steps: [
        {
          iconComponent: <IconHeartHand size={24} />,
          title: 'autocutsilences.tutorial.steps.define.title',
          description: 'autocutsilences.tutorial.steps.define.description',
          imagePath: (
            <video key={`caption-autocut`} autoPlay muted loop playsInline>
              <source
                src={'/static/features/silences/silencesStep1.mp4'}
                type="video/mp4"
              />
            </video>
          ),
        },
        {
          iconComponent: <IconTranslate02 size={24} />,
          title: 'autocutsilences.tutorial.steps.delete.title',
          description: 'autocutsilences.tutorial.steps.delete.description',
          imagePath: (
            <video key={`caption-autocut`} autoPlay muted loop playsInline>
              <source
                src={'/static/features/silences/silencesStep2.mp4'}
                type="video/mp4"
              />
            </video>
          ),
        },
      ],
    },
  },
  autocutpodcast: {
    title: 'autocutpodcast.title',
    subtitle: 'autocutpodcast.subtitle',
    demoUrl: 'autocutpodcast.video.url',
    demoTitle: 'autocutpodcast.video.title',
    videoComponent: scrollPosition => (
      <PodcastVideo scrollPosition={scrollPosition} loop />
    ),
    compatibilities: CompatibilityContentPremiereProAndDavinciResolve,
    features: [
      {
        title: 'autocutpodcast.features.multitracks.title',
        description: 'autocutpodcast.features.multitracks.description',
        iconComponent: <IconZapFast size={24} />,
      },
      {
        title: 'autocutpodcast.features.formats.title',
        description: 'autocutpodcast.features.formats.description',
        iconComponent: <IconZapFast size={24} />,
      },
      {
        title: 'autocutpodcast.features.jcuts.title',
        description: 'autocutpodcast.features.jcuts.description',
        iconComponent: <IconTranslate02 size={24} />,
      },
    ],
    tutorial: {
      title: 'autocutpodcast.tutorial.title',
      steps: [
        {
          iconComponent: <IconHeartHand size={24} />,
          title: 'autocutpodcast.tutorial.steps.define.title',
          description: 'autocutpodcast.tutorial.steps.define.description',
          imagePath: (
            <Image
              alt="tutorial"
              src={'/static/features/podcast/podcastStep1.png'}
              fill
            />
          ),
        },
        {
          iconComponent: <IconTranslate02 size={24} />,
          title: 'autocutpodcast.tutorial.steps.delete.title',
          description: 'autocutpodcast.tutorial.steps.delete.description',
          imagePath: (
            <Image
              alt="tutorial"
              src={'/static/features/podcast/podcastStep2.png'}
              fill
            />
          ),
        },
        {
          iconComponent: <IconTranslate02 size={24} />,
          title: 'autocutpodcast.tutorial.steps.edit.title',
          description: 'autocutpodcast.tutorial.steps.edit.description',
          imagePath: (
            <video key={`caption-autocut`} autoPlay muted loop playsInline>
              <source
                src={'/static/features/podcast/podcastStep3.mp4'}
                type="video/mp4"
              />
            </video>
          ),
        },
      ],
    },
  },
  autozoom: {
    title: 'autozoom.title',
    subtitle: 'autozoom.subtitle',
    demoUrl: 'autozoom.video.url',
    demoTitle: 'autozoom.video.title',
    videoComponent: scrollPosition => (
      <ZoomVideo scrollPosition={scrollPosition} loop />
    ),
    compatibilities: CompatibilityContentPremiereProAndDavinciResolve,
    features: [
      {
        title: 'autozoom.features.preview.title',
        description: 'autozoom.features.preview.description',
        iconComponent: <IconZapFast size={24} />,
      },
      {
        title: 'autozoom.features.parameters.title',
        description: 'autozoom.features.parameters.description',
        iconComponent: <IconZapFast size={24} />,
      },
    ],
    tutorial: {
      title: 'autozoom.tutorial.title',
      steps: [
        {
          iconComponent: <IconHeartHand size={24} />,
          title: 'autozoom.tutorial.steps.define.title',
          description: 'autozoom.tutorial.steps.define.description',
          imagePath: (
            <video key={`autozoom`} autoPlay muted loop playsInline>
              <source
                src={'/static/features/autozoom/autozoomStep1.mp4'}
                type="video/mp4"
              />
            </video>
          ),
        },
        {
          iconComponent: <IconTranslate02 size={24} />,
          title: 'autozoom.tutorial.steps.delete.title',
          description: 'autozoom.tutorial.steps.delete.description',
          imagePath: (
            <video key={`autozoom`} autoPlay muted loop playsInline>
              <source
                src={'/static/features/autozoom/autozoomStep2.mp4'}
                type="video/mp4"
              />
            </video>
          ),
        },
        {
          iconComponent: <IconTranslate02 size={24} />,
          title: 'autozoom.tutorial.steps.edit.title',
          description: 'autozoom.tutorial.steps.edit.description',
          imagePath: (
            <video key={`autozoom`} autoPlay muted loop playsInline>
              <source
                src={'/static/features/autozoom/autozoomStep3.mp4'}
                type="video/mp4"
              />
            </video>
          ),
        },
      ],
    },
  },
  autoresize: {
    title: 'autoresize.title',
    subtitle: 'autoresize.subtitle',
    //demoTitle: 'autoresize.video.title',
    videoComponent: scrollPosition => (
      <ResizeVideo scrollPosition={scrollPosition} loop />
    ),
    compatibilities: CompatibilityContentPremierePro,
    features: [
      {
        title: 'autoresize.features.preview.title',
        description: 'autoresize.features.preview.description',
        iconComponent: <IconZapFast size={24} />,
      },
      {
        title: 'autoresize.features.parameters.title',
        description: 'autoresize.features.parameters.description',
        iconComponent: <IconZapFast size={24} />,
      },
    ],
    tutorial: {
      title: 'autoresize.tutorial.title',
      steps: [
        {
          iconComponent: <IconHeartHand size={24} />,
          title: 'autoresize.tutorial.steps.define.title',
          description: 'autoresize.tutorial.steps.define.description',
          imagePath: (
            <Image
              alt="tutorial"
              src={'/static/features/autoresize/autoresizeStep1.png'}
              fill
            />
          ),
        },
        {
          iconComponent: <IconTranslate02 size={24} />,
          title: 'autoresize.tutorial.steps.delete.title',
          description: 'autoresize.tutorial.steps.delete.description',
          imagePath: (
            <video key={`autoresize`} autoPlay muted loop playsInline>
              <source
                src={'/static/features/autoresize/autoresizeStep2.mp4'}
                type="video/mp4"
              />
            </video>
          ),
        },
      ],
    },
  },
  autocutrepeat: {
    title: 'autocutrepeat.title',
    subtitle: 'autocutrepeat.subtitle',
    //demoTitle: 'autocutrepeat.video.title',
    videoComponent: () => (
      <div style={{ height: '400px', width: '650px' }}>
        <Image
          alt="tutorial"
          src={'/static/features/autocutrepeat/repeatHero.png'}
          fill
          style={{ borderRadius: '3%' }}
        />
      </div>
    ),
    compatibilities: CompatibilityContentPremiereProAndDavinciResolve,
    features: [
      {
        title: 'autocutrepeat.features.preview.title',
        description: 'autocutrepeat.features.preview.description',
        iconComponent: <IconZapFast size={24} />,
      },
      {
        title: 'autocutrepeat.features.parameters.title',
        description: 'autocutrepeat.features.parameters.description',
        iconComponent: <IconZapFast size={24} />,
        cta: {
          title: 'autocutrepeat.features.parameters.ctaTitle',
          url: '/help-center/q-a/supported-languages/',
        },
      },
    ],
    tutorial: {
      title: 'autocutrepeat.tutorial.title',
      steps: [
        {
          iconComponent: <IconHeartHand size={24} />,
          title: 'autocutrepeat.tutorial.steps.define.title',
          description: 'autocutrepeat.tutorial.steps.define.description',
          imagePath: (
            <Image
              alt="tutorial"
              src={'/static/features/autocutrepeat/autocutrepeatStep1.png'}
              fill
            />
          ),
        },
        {
          iconComponent: <IconTranslate02 size={24} />,
          title: 'autocutrepeat.tutorial.steps.edit.title',
          description: 'autocutrepeat.tutorial.steps.edit.description',
          imagePath: (
            <Image
              alt="tutorial"
              src={'/static/features/autocutrepeat/autocutrepeatStep2.png'}
              fill
            />
          ),
        },
        {
          iconComponent: <IconTranslate02 size={24} />,
          title: 'autocutrepeat.tutorial.steps.delete.title',
          description: 'autocutrepeat.tutorial.steps.delete.description',
          imagePath: (
            <video key={`autocutrepeat`} autoPlay muted loop playsInline>
              <source
                src={'/static/features/autocutrepeat/autocutrepeatStep3.mp4'}
                type="video/mp4"
              />
            </video>
          ),
        },
      ],
    },
  },
  autoprofanityfilter: {
    title: 'autoprofanity.title',
    subtitle: 'autoprofanity.subtitle',
    //demoTitle: 'autoprofanity.video.title',
    videoComponent: () => (
      <div style={{ height: '400px', width: '650px' }}>
        <Image
          alt="tutorial"
          src={'/static/features/autoprofanity/autoprofanityHero.png'}
          fill
        />
      </div>
    ),
    compatibilities: CompatibilityContentPremiereProAndDavinciResolve,
    features: [
      {
        title: 'autoprofanity.features.preview.title',
        description: 'autoprofanity.features.preview.description',
        iconComponent: <IconZapFast size={24} />,
      },
      {
        title: 'autoprofanity.features.parameters.title',
        description: 'autoprofanity.features.parameters.description',
        iconComponent: <IconZapFast size={24} />,
        cta: {
          title: 'autoprofanity.features.parameters.ctaTitle',
          url: '/help-center/q-a/supported-languages/',
        },
      },
    ],
    tutorial: {
      title: 'autoprofanity.tutorial.title',
      steps: [
        {
          iconComponent: <IconHeartHand size={24} />,
          title: 'autoprofanity.tutorial.steps.define.title',
          description: 'autoprofanity.tutorial.steps.define.description',
          imagePath: (
            <Image
              alt="tutorial"
              src={'/static/features/autoprofanity/autoprofanityStep1.png'}
              fill
            />
          ),
        },
        {
          iconComponent: <IconTranslate02 size={24} />,
          title: 'autoprofanity.tutorial.steps.delete.title',
          description: 'autoprofanity.tutorial.steps.delete.description',
          imagePath: (
            <video key={`autoprofanity`} autoPlay muted loop playsInline>
              <source
                src={'/static/features/autoprofanity/autoprofanityStep2.mp4'}
                type="video/mp4"
              />
            </video>
          ),
        },
      ],
    },
  },
  autobroll: {
    title: 'autobroll.title',
    subtitle: 'autobroll.subtitle',
    demoUrl: 'autobroll.video.url',
    demoTitle: 'autobroll.video.title',
    videoComponent: () => (
      <div style={{ height: '400px', width: '650px' }}>
        <Image
          alt="tutorial"
          src={'/static/features/autobroll/autobrollHero.png'}
          fill
        />
      </div>
    ),
    compatibilities: CompatibilityContentPremiereProAndDavinciResolve,
    features: [
      {
        title: 'autobroll.features.preview.title',
        description: 'autobroll.features.preview.description',
        iconComponent: <IconZapFast size={24} />,
      },
      {
        title: 'autobroll.features.parameters.title',
        description: 'autobroll.features.parameters.description',
        cta: {
          title: 'autoprofanity.features.parameters.ctaTitle',
          url: '/help-center/q-a/supported-languages/',
        },
        iconComponent: <IconZapFast size={24} />,
      },
    ],
    tutorial: {
      title: 'autobroll.tutorial.title',
      steps: [
        {
          iconComponent: <IconHeartHand size={24} />,
          title: 'autobroll.tutorial.steps.define.title',
          description: 'autobroll.tutorial.steps.define.description',
          imagePath: (
            <Image
              alt="tutorial"
              src={'/static/features/autobroll/autobrollStep1.png'}
              fill
            />
          ),
        },
        {
          iconComponent: <IconTranslate02 size={24} />,
          title: 'autobroll.tutorial.steps.search.title',
          description: 'autobroll.tutorial.steps.search.description',
          imagePath: (
            <video key={`autobroll`} autoPlay muted loop playsInline>
              <source
                src={'/static/features/autobroll/autobrollStep2.mp4'}
                type="video/mp4"
              />
            </video>
          ),
        },
        {
          iconComponent: <IconTranslate02 size={24} />,
          title: 'autobroll.tutorial.steps.launch.title',
          description: 'autobroll.tutorial.steps.launch.description',
          imagePath: (
            <video key={`autobroll`} autoPlay muted loop playsInline>
              <source
                src={'/static/features/autobroll/autobrollStep3.mp4'}
                type="video/mp4"
              />
            </video>
          ),
        },
      ],
    },
  }, //'autoviral','autochapters',
  autoviral: {
    title: 'autoviral.title',
    subtitle: 'autoviral.subtitle',
    //demoTitle: 'autoprofanity.video.title',
    videoComponent: () => (
      <div style={{ height: '400px', width: '650px' }}>
        <Image
          alt="tutorial"
          src={'/static/features/autoviral/autoviralHero.png'}
          fill
        />
      </div>
    ),
    compatibilities: CompatibilityContentPremiereProAndDavinciResolve,
    features: [
      {
        title: 'autoviral.features.preview.title',
        description: 'autoviral.features.preview.description',
        iconComponent: <IconZapFast size={24} />,
      },
      {
        title: 'autoviral.features.parameters.title',
        description: 'autoviral.features.parameters.description',
        iconComponent: <IconZapFast size={24} />,
        cta: {
          title: 'autoprofanity.features.parameters.ctaTitle',
          url: '/help-center/q-a/supported-languages/',
        },
      },
    ],
    tutorial: {
      title: 'autoviral.tutorial.title',
      steps: [
        {
          iconComponent: <IconHeartHand size={24} />,
          title: 'autoviral.tutorial.steps.define.title',
          description: 'autoviral.tutorial.steps.define.description',
          imagePath: (
            <Image
              alt="tutorial"
              src={'/static/features/autoviral/Step1-AutoViral.png'}
              fill
            />
          ),
        },
        {
          iconComponent: <IconTranslate02 size={24} />,
          title: 'autoviral.tutorial.steps.search.title',
          description: 'autoviral.tutorial.steps.search.description',
          imagePath: (
            <video key={`autoviral`} autoPlay muted loop playsInline>
              <source
                src={'/static/features/autoviral/Step2-AutoViral.mp4'}
                type="video/mp4"
              />
            </video>
          ),
        },
        {
          iconComponent: <IconTranslate02 size={24} />,
          title: 'autoviral.tutorial.steps.launch.title',
          description: 'autoviral.tutorial.steps.launch.description',
          imagePath: (
            <video key={`autoviral`} autoPlay muted loop playsInline>
              <source
                src={'/static/features/autoviral/Step3-AutoViral.mp4'}
                type="video/mp4"
              />
            </video>
          ),
        },
      ],
    },
  },
  autochapters: {
    title: 'autochapters.title',
    subtitle: 'autochapters.subtitle',
    videoComponent: () => (
      <div style={{ height: '400px', width: '650px' }}>
        <Image
          alt="tutorial"
          src={'/static/features/autochapters/autochaptersHero.png'}
          fill
        />
      </div>
    ),
    compatibilities: CompatibilityContentPremiereProAndDavinciResolve,
    features: [
      {
        title: 'autochapters.features.preview.title',
        description: 'autochapters.features.preview.description',
        iconComponent: <IconZapFast size={24} />,
      },
      {
        title: 'autochapters.features.parameters.title',
        description: 'autochapters.features.parameters.description',
        cta: {
          title: 'autoprofanity.features.parameters.ctaTitle',
          url: '/help-center/q-a/supported-languages/',
        },
        iconComponent: <IconZapFast size={24} />,
      },
    ],
    tutorial: {
      title: 'autochapters.tutorial.title',
      steps: [
        {
          iconComponent: <IconHeartHand size={24} />,
          title: 'autochapters.tutorial.steps.define.title',
          description: 'autochapters.tutorial.steps.define.description',
          imagePath: (
            <Image
              alt="tutorial"
              src={'/static/features/autochapters/Step1-AutoChapters.png'}
              fill
            />
          ),
        },
        {
          iconComponent: <IconTranslate02 size={24} />,
          title: 'autochapters.tutorial.steps.search.title',
          description: 'autochapters.tutorial.steps.search.description',
          imagePath: (
            <video key={`autochapters`} autoPlay muted loop playsInline>
              <source
                src={'/static/features/autochapters/Step2-AutoChapters.mp4'}
                type="video/mp4"
              />
            </video>
          ),
        },
        {
          iconComponent: <IconTranslate02 size={24} />,
          title: 'autochapters.tutorial.steps.launch.title',
          description: 'autochapters.tutorial.steps.launch.description',
          imagePath: (
            <video key={`autochapters`} autoPlay muted loop playsInline>
              <source
                src={'/static/features/autochapters/Step3-AutoChapters.mp4'}
                type="video/mp4"
              />
            </video>
          ),
        },
      ],
    },
  },
};

export const FeaturePage = ({
  feature,
  currentLocale,
}: {
  feature: (typeof FEATURES)[number];
  currentLocale: string;
}) => {
  const t = useTranslations('page.features');

  const featureData = data[feature];

  return (
    <>
      <FeatureHeroSection
        title={t(featureData.title)}
        subtitle={t(featureData.subtitle)}
        demoUrl={featureData.demoUrl && t(featureData.demoUrl)}
        demoTitle={featureData.demoTitle && t(featureData.demoTitle)}
        videoComponent={featureData.videoComponent}
        compatibilities={featureData.compatibilities}
        currentLocale={currentLocale}
      />
      <KeziosFeatureSection>
        {featureData.features.map((feature, index) => (
          <KeziosFeatureSection.Item
            key={index}
            title={t(feature.title)}
            description={t(feature.description)}
            icon={feature.iconComponent}
            cta={
              feature.cta && (
                <Button
                  variant="underline"
                  component={Link}
                  href={feature.cta.url}
                  locale={currentLocale}
                >
                  {t(feature.cta.title)}
                </Button>
              )
            }
            titleComponent={'h3'}
          />
        ))}
      </KeziosFeatureSection>
      <InfoSectionLayout title={t(featureData.tutorial.title)}>
        {featureData.tutorial.steps.map((tutorial, index) => (
          <InfoSectionLayout.Item
            key={index}
            title={t(tutorial.title)}
            description={t(tutorial.description)}
            icon={tutorial.iconComponent}
            image={tutorial.imagePath}
            reverse={index % 2 !== 0}
            animatedTitle={tutorial.animatedTitle}
          />
        ))}
      </InfoSectionLayout>
    </>
  );
};
